/**
 * Offline Indicator Component
 *
 * Displays a banner when the app is offline and shows queued operations count.
 *
 * Requirements: 5.4
 */

import React from 'react'
import { useNetworkStatus } from '../hooks/useNetworkStatus'
import './OfflineIndicator.css'

/**
 * Offline Indicator Props
 */
export interface OfflineIndicatorProps {
    /** Custom class name */
    className?: string

    /** Whether to show queued operations count */
    showQueuedCount?: boolean
}

/**
 * Offline Indicator Component
 *
 * Shows a banner at the top of the screen when offline.
 */
export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
    className = '',
    showQueuedCount = true,
}) => {
    const { isOnline, queuedOperations } = useNetworkStatus()

    // Don't render anything if online
    if (isOnline) {
        return null
    }

    return (
        <div
            className={`offline-indicator ${className}`}
            role="alert"
            aria-live="polite"
        >
            <div className="offline-indicator__content">
                <svg
                    className="offline-indicator__icon"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <path
                        d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2ZM10 16C6.68629 16 4 13.3137 4 10C4 6.68629 6.68629 4 10 4C13.3137 4 16 6.68629 16 10C16 13.3137 13.3137 16 10 16Z"
                        fill="currentColor"
                    />
                    <path
                        d="M10 6C9.44772 6 9 6.44772 9 7V11C9 11.5523 9.44772 12 10 12C10.5523 12 11 11.5523 11 11V7C11 6.44772 10.5523 6 10 6Z"
                        fill="currentColor"
                    />
                    <path
                        d="M10 13C9.44772 13 9 13.4477 9 14C9 14.5523 9.44772 15 10 15C10.5523 15 11 14.5523 11 14C11 13.4477 10.5523 13 10 13Z"
                        fill="currentColor"
                    />
                </svg>

                <div className="offline-indicator__text">
                    <span className="offline-indicator__title">
                        You're offline
                    </span>
                    {showQueuedCount && queuedOperations > 0 && (
                        <span className="offline-indicator__queue">
                            {queuedOperations}{' '}
                            {queuedOperations === 1
                                ? 'operation'
                                : 'operations'}{' '}
                            queued
                        </span>
                    )}
                </div>
            </div>
        </div>
    )
}
